import { useCallback, useMemo } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import { CommonModal } from "../../../components";
import { Form } from "../../../components/ui/common/form";
import { ScrollArea } from "../../../components/ui/common/scroll-area";
import AddAnotherIngredientButton from "./components/AddAnotherIngredientButton";
import AddIngredientFooter from "./components/AddIngredientFooter";
import IngredientCapacityStatus from "./components/IngredientCapacityStatus";
import IngredientDraftRow from "./components/IngredientDraftRow";
import { useIngredientOptions } from "./hooks/useIngredientOptions";
import { useRecipeAddIngredientForm } from "./hooks/useRecipeAddIngredientForm";
import { RecipeAddIngredientModalProps } from "./types/recipeAddIngredient.types";

const RecipeAddIngredientModalNew = ({
  visibleModal,
  setVisibleModal,
  recipeDataRefetch,
  recipeId,
  calculatedProductSalesUnit,
  genericIngredient: selectedGenericIngredient,
  calculatedIngredient,
  usedColors
}: RecipeAddIngredientModalProps) => {
  const resolvedCalculatedIngredient = calculatedIngredient ?? [];

  const selectedSalesUnitIds = useMemo(
    () => calculatedProductSalesUnit.map(item => item.salesUnitId),
    [calculatedProductSalesUnit]
  );

  const selectedGenericIngredientIds = useMemo(
    () => selectedGenericIngredient.map(item => item.ingredientId),
    [selectedGenericIngredient]
  );

  const { productUnitOptions, genericOptions, isLoading } = useIngredientOptions({
    selectedSalesUnitIds,
    selectedGenericIngredientIds
  });

  const {
    form,
    control,
    fields,
    drafts,
    hasPending,
    hasSaved,
    pendingCount,
    remaining,
    savedTotal,
    pendingTotal,
    isSaving,
    resetForm,
    handleIngredientTypeChange,
    handlePercentageChange,
    saveRow,
    saveRowAndAddNew,
    removeRow,
    addRow,
    setDescriptionOpen
  } = useRecipeAddIngredientForm({
    visibleModal,
    recipeId,
    recipeDataRefetch,
    usedColors,
    calculatedProductSalesUnit,
    genericIngredient: selectedGenericIngredient,
    calculatedIngredient: resolvedCalculatedIngredient,
    productUnitOptions,
    genericOptions
  });

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        resetForm();
        setVisibleModal(false);
      }
    },
    [resetForm, setVisibleModal]
  );

  const handleFooterClose = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  if (isLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <CommonModal
      modalOpenState={visibleModal}
      modalCloseState={handleClose}
      title="Add Ingredients"
      contentClassName="!tw-w-full sm:!tw-max-w-6xl"
      modalDescription={
        <IngredientCapacityStatus
          remaining={remaining}
          savedTotal={savedTotal}
          pendingTotal={pendingTotal}
        />
      }
    >
      <Form {...form}>
        <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
          <ScrollArea className="tw-flex-1 tw-px-6 tw-py-5">
            <div className="tw-space-y-4 tw-pb-4">
              {fields.map((field, index) => {
                const draft = drafts[index];
                if (!draft) return null;

                return (
                  <IngredientDraftRow
                    key={field.id}
                    index={index}
                    draft={draft}
                    control={control}
                    productUnitOptions={productUnitOptions}
                    genericOptions={genericOptions}
                    pendingCount={pendingCount}
                    hasSaved={hasSaved}
                    isSaving={isSaving}
                    onIngredientTypeChange={handleIngredientTypeChange}
                    onPercentageChange={handlePercentageChange}
                    onDescriptionOpenChange={setDescriptionOpen}
                    onSave={rowIndex => void saveRow(rowIndex)}
                    onSaveAndAddNew={saveRowAndAddNew}
                    onRemove={removeRow}
                  />
                );
              })}

              <AddAnotherIngredientButton
                disabled={hasPending}
                onClick={addRow}
                showPendingHint={hasPending}
              />
            </div>
          </ScrollArea>

          <AddIngredientFooter
            remaining={remaining}
            savedTotal={savedTotal}
            hasSaved={hasSaved}
            hasPending={hasPending}
            onClose={handleFooterClose}
          />
        </div>
      </Form>
    </CommonModal>
  );
};

export default RecipeAddIngredientModalNew;
